/**
 * 環境識別バナーコンポーネント
 *
 * 環境ごとに色を変えて視覚的に環境を識別可能にする。
 * - dev: 青 (bg-blue-500)
 * - stg: 赤 (bg-red-500)
 * - prod: 非表示
 * - その他: 黄色 (bg-yellow-500)
 */

import { useRef } from "react";
import { ENV } from "../../config/env";
import {
  isFreezeRecorderInstalled,
  requestFreezeReport,
} from "../../lib/freezeRecorder";

/** 色帯の長押しで固まりログのカードを開くまでの時間（PRODUCT_DEV-1115） */
const LONG_PRESS_MS = 700;

const ENVIRONMENT_COLORS: Record<string, string> = {
  dev: "bg-blue-500",
  stg: "bg-red-500",
  prod: "", // 本番環境では非表示
};

export function EnvironmentBanner() {
  const env = ENV.VITE_ENV;

  // 本番環境では非表示
  if (env === "prod") {
    return null;
  }

  // 環境に応じたカラークラスを取得（デフォルトは黄色）
  const colorClass = ENVIRONMENT_COLORS[env] || "bg-yellow-500";

  return (
    <>
      <div
        className={`fixed top-0 left-0 right-0 h-[3px] ${colorClass} z-[9999]`}
        role="banner"
        aria-label={`Environment: ${env}`}
      />
      {isFreezeRecorderInstalled() && <FreezeReportHandle />}
    </>
  );
}

/**
 * PRODUCT_DEV-1115: 固まりを検知していなくてもログを取り出せるよう、色帯の中央を長押しすると
 * 固まりログのカードを開く。3px の帯は指で押せないので、押せる幅を上端の中央にだけ広げる
 * （ヘッダーはロゴが左・アイコンが右で、上端の中央は空いている）。固まり記録が動く DEV だけ。
 */
function FreezeReportHandle() {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  return (
    <div
      aria-hidden
      className="fixed top-0 left-1/2 z-[9999] h-3.5 w-32 -translate-x-1/2 select-none"
      style={{ touchAction: "none", WebkitTouchCallout: "none" }}
      onPointerDown={() => {
        cancel();
        timer.current = setTimeout(() => {
          timer.current = null;
          requestFreezeReport();
        }, LONG_PRESS_MS);
      }}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
}
